import { useContext, useEffect, useState } from "react";
import {
  getTenants,
  createTenants,
  updateTenants,
  deleteTenant,
  reactiveTenants,
  getTenantsByParentId,
  getListTenantsWithApplications,
} from "../services/console/tenant";
import { AppContext } from "../context";
import { useNavigate } from "react-router-dom";
import { ALIAS_PARENT } from "../constants";

type UseType = "DATA" | "ACTIONS" | "TENANTID" | "CHILDREN" | "LISTAPPS";
type ActionType = "CREATE" | "UPDATE" | "DELETE" | "REACTIVE";

const handleTenantActionFunction = (action: ActionType, t: Function) => {
  switch (action) {
    case "CREATE":
      return {
        actionFn: createTenants,
        fnMsg: t("Tenants.apiCreateLabel"),
        actionType: action
      };
    case "UPDATE":
      return {
        actionFn: updateTenants,
        fnMsg: t("Tenants.apiUpdateLabel"),
        actionType: action
      };
    case "DELETE":
      return {
        actionFn: deleteTenant,
        fnMsg: t("Tenants.apiDeleteLabel"),
        actionType: action
      };
    case "REACTIVE":
      return {
        actionFn: reactiveTenants,
        fnMsg: t("Tenants.apiReactiveLabel"),
        actionType: action
      };
    default:
      return {
        actionFn: () => {},
        fnMsg: "",
        actionType: ""
      };
  }
};

export const useTenants = (type: UseType) => {
  const {
    t,
    toast,
    appState: { selectedApp, tenantId, tenantIdListsNavigations, selectedTenantId },
    setAppState,
  } = useContext(AppContext);
  const [data, setData] = useState<any | null>(null);
  const [listApps, setListApps] = useState<any | null>(null);
  const [reload, setReload] = useState<number>(0);
  const appid = selectedApp?.appid || "0";
  const tenantid = tenantId || "0";
  const navigate = useNavigate();

  const reloadFn = () => {
    setData(null);
    setReload((prev) => prev + 1);
  };

  const tenantAction = async (action: ActionType, data: any) => {
    const { actionFn, fnMsg, actionType } = handleTenantActionFunction(action, t);
    setAppState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    // TOAST DATA VARIABLE
    let toastData:{} | null = null
    const actionStatus = await Promise.resolve(actionFn(data))
      .then((_) => {
        toastData = {
          severity: "success",
          summary: "",
          detail: `${fnMsg}`,
          life: 3000,
        }
        if (actionType !== 'UPDATE') {
          toast.current.replace(toastData);
        }
        return "SUCCESS";
      })
      .catch((error) => {
        toastData = {
          severity: "error",
          summary: "",
          detail: error?.response?.data?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
          life: 3000,
        }
        if (actionType !== 'UPDATE') {
          toast.current.replace(toastData);
        }
        return "ERROR";
      });
    setAppState((prev) => ({
      ...prev,
      isLoading: false,
      toastData,
    }));
    if (actionType === 'UPDATE') {
      navigate('/tenants')
    }
    return actionStatus;
  };

  useEffect(() => {
    if (type === "DATA" && appid) {
      (async function () {
        await Promise.resolve(getTenants(selectedTenantId || tenantId))
          .then((response) => {
            setData(response?.data);
          })
          .catch((error) => {
            toast.current.replace({
              severity: "error",
              summary: "",
              detail: error?.response?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
              life: 3000,
            });
          });
      })();
    }
  }, [appid, toast, reload, type]);

  useEffect(() => {
    if (type === "TENANTID" && tenantid) {
      setAppState((prev) => ({
        ...prev,
        tenantIdListsNavigations: [...new Set([...tenantIdListsNavigations, tenantid])],
      }));
      (async function () {
        await Promise.resolve(getTenantsByParentId(selectedTenantId ? selectedTenantId : tenantid))
          .then((response) => {
            const data = response?.data;

            sessionStorage.setItem(ALIAS_PARENT, (!!data && data[0]?.alias) ?? '');

            if (data[0].maintenant === true) {
              setAppState((prev) => ({
                ...prev,
                isMainTenant: true,
              }));
            }
            if (data[0].childrenTenants.length > 0) {
              setData(data);
              setAppState((prev) => ({
                ...prev,
                isParentTenant: true,
                tenantParentName: data[0].name,
                showChangeClientButton: true,
              }));
            } else {
              setData(null);
              setAppState((prev) => ({
                ...prev,
                selectedTenantId: selectedTenantId ? selectedTenantId : tenantid,
                isParentTenant: false,
                tenantParentName: data[0].name,
                aliasParentTenant: data[0].alias,
              }));
            }
          })
          .catch((error) => {
            toast.current.replace({
              severity: "error",
              summary: "",
              detail: error?.response?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
              life: 3000,
            });
          });
      })();
    }
  }, [tenantid, toast, reload, type]);
  
  useEffect(() => {
    if (type === "LISTAPPS" && tenantid) {
      (async function () {
        await Promise.resolve(getListTenantsWithApplications(selectedTenantId || tenantId))
          .then((response) => {
            setListApps(response?.data);
          })
          .catch((error) => {
            setListApps(null);
          });
      })();
    }
  }, [tenantid, toast, type, reload]);

  const getInfoChildrenTenants = async (tenantid: string) => {
    if (type === "CHILDREN"){
      return await Promise.resolve(getTenantsByParentId(tenantid))
        .then(async (response) => {
          setAppState((prev) => ({
            ...prev,
            isMainTenant: response?.data[0]?.maintenant,
          }));
          return await response?.data;
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }

  return {
    data,
    tenantAction,
    reloadFn,
    getInfoChildrenTenants,
    listApps
  };
};
